// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  CITATION_ATTR,
  DEAD_LINK_ATTR,
  LINK_ELEMENTS,
  describeDeadLink,
  isNavigatingHref,
  kbUrlnameFromHref,
  prepareArticleHtml,
} from "../utils/kbArticleHtml";
import { ARTICLE_MESSAGES, classifyArticleResponse } from "../utils/kbArticleFetch";
import environment from "../config/environment";
import "./CitationTwo.css";

/**
 * The Copilot (beta) KB citation slide-out — the AVS OS citation interface (ACOP-93).
 *
 * A NEW, parallel, self-contained interface, named to match CopilotTwo / the /copilot-2 route and
 * the c2- class prefix. The existing Copilot has no citation UI of its own and is deliberately
 * untouched: the AVS OS RAG and KB are being built side by side with the older system rather than
 * replacing it in place, so nothing here is shared with, or reusable by, the standard customer
 * Copilot (David, 2026-08-20). Only CopilotTwo.tsx imports it.
 *
 * Split out of CopilotTwo.tsx so it can be tested. CopilotTwo imports react-markdown and
 * react-router-dom, and neither resolves under react-scripts' Jest — react-markdown is ESM-only
 * and react-router-dom v7 publishes an exports map Jest cannot read. Since the guarantee this
 * interface makes is behavioural rather than structural — **no click anywhere in it may leave
 * Copilot** — being able to prove it by clicking is worth more than keeping one large file.
 */

// ─── Citation panel (popover-style side drawer) ───────────────────────────────

/** Resolve relative /apis/... path to the absolute API origin. */
function toAbsApiUrl(relOrAbs: string): string {
  if (!relOrAbs || relOrAbs.startsWith("http")) return relOrAbs;
  // BASE_URL ends with "/apis/" — strip leading "/apis/" from the path
  return environment.BASE_URL + relOrAbs.replace(/^\/apis\//, "");
}

/**
 * The in-panel dead page (ACOP-93).
 *
 * Articles reference each other by Salesforce urlname while the knowledge base is still being
 * back-filled, so a reference to an article that does not exist is routine. The requirement is
 * that such a click NEVER leaves Copilot: it lands here, naming the article, so the broken
 * reference is surfaced during testing instead of silently opening another site.
 *
 * A React subtree rather than injected markup, so it cannot contain a live link by construction.
 */
function DeadArticlePage({ entry, canGoBack, onBack }) {
  return (
    <div className="c2-dead-page">
      <div className="c2-dead-icon">
        {/* fa-circle-question, not fa-file-circle-question: the vendored Font Awesome 6.5.1
            build is subsetted and does not contain file-circle-question in EITHER weight, so it
            rendered as nothing here from ACOP-93 until ACOP-100 caught it. Verified by rasterising
            the glyph against an unassigned codepoint — a width check is misleading, because most FA
            glyphs are exactly 1em wide and so is the missing-glyph box. */}
        <i className="fa-regular fa-circle-question" aria-hidden />
      </div>
      <p className="c2-dead-title">{entry.label} is not in the knowledge base</p>
      <p className="c2-dead-body">
        This article is referenced by the one you were reading, but it has not been published to
        the knowledge base yet. Nothing is broken on your end.
      </p>
      {entry.urlname && <div className="c2-dead-ref">{entry.urlname}</div>}
      <div className="c2-dead-actions">
        {canGoBack && (
          <button type="button" className="c2-panel-btn" onClick={onBack}>
            ← Back to the article
          </button>
        )}
      </div>
    </div>
  );
}

/** The AVSOS-side path a citation URL wraps, which is where the urlname can be read from. */
function citationPathOf(citationUrl: string): string {
  try {
    return new URL(citationUrl, "https://placeholder.invalid").searchParams.get("url") || "";
  } catch {
    return "";
  }
}

/**
 * CSS.escape with a fallback.
 *
 * Every branch of the click handler calls preventDefault before it reaches a selector, so a throw
 * here cannot let a click escape — but it would still break in-page scrolling, and CSS.escape is
 * absent under jsdom, where the click interception is tested. The value is interpolated into an
 * [id="..."] selector, so quoting and backslashes are what have to be escaped.
 */
function escapeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/["\\]/g, "\\$&");
}

/**
 * @param onOpenFullArticle  Called with the article's KB urlname when the reader asks for the
 *   full-size view (ACOP-100). A CALLBACK, not an href, and not a route push from in here: this
 *   component deliberately does not import react-router-dom — that is why it could be split out of
 *   CopilotTwo and tested at all (see the header). The owner does the navigating; see CopilotTwo.
 *   Optional, so the panel still renders without a way to open the full view.
 */
export function CitationTwo({ initialSource, onClose, onOpenFullArticle }) {
  /*
   * The panel's history. Each entry is either an article to fetch or a reference that resolves to
   * nothing, so Back walks the same chain either way and a dead end is a normal step rather than a
   * special case: article -> article -> dead page -> Back -> Back -> chat.
   */
  const [stack, setStack] = useState([
    {
      kind: "article",
      url: toAbsApiUrl(initialSource.citationUrl),
      path: citationPathOf(initialSource.citationUrl),
      label: initialSource.title || "Knowledge Base",
    },
  ]);
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleTitle, setArticleTitle] = useState(initialSource.title || "Knowledge Base");
  const contentRef = useRef(null);

  const current = stack[stack.length - 1];
  const canGoBack = stack.length > 1;
  const isDead = current.kind === "dead";

  /*
   * The urlname the full-size view would be deep-linked to, or "" when there is nothing to open.
   * Empty on the dead page by construction — there is no article there to open full size — and
   * empty for any entry whose path yields no urlname, which keeps the affordance from appearing
   * over a destination it cannot actually reach.
   */
  const fullArticleUrlname = isDead ? "" : kbUrlnameFromHref(current.path || "");

  const goBack = useCallback(() => setStack((p) => p.slice(0, -1)), []);
  const push = useCallback((entry) => setStack((p) => [...p, entry]), []);

  /** Turn the entry being viewed into the dead page, so Back still returns to where it came from. */
  const markCurrentDead = useCallback(() => {
    setStack((p) => {
      const top = p[p.length - 1];
      return [
        ...p.slice(0, -1),
        { kind: "dead", ...describeDeadLink(top.path || "", top.label) },
      ];
    });
  }, []);

  // Fetch article HTML whenever the current entry changes
  useEffect(() => {
    if (isDead) {
      setHtml(null);
      setError(null);
      setLoading(false);
      setArticleTitle(current.label);
      return undefined;
    }
    if (!current.url) return undefined;

    let cancelled = false;
    setLoading(true);
    setHtml(null);
    setError(null);
    setArticleTitle(current.label);

    fetch(current.url, {
      credentials: "include",
      headers: { "Authorization": "Bearer " + environment.BEARER_TOKEN },
    })
      .then(async (r) => ({
        ok: r.ok,
        status: r.status,
        // content-type is CORS-safelisted, so this is readable cross-origin without any
        // Access-Control-Expose-Headers cooperation from the Function App.
        isJson: (r.headers.get("content-type") || "").includes("application/json"),
        body: await r.text(),
      }))
      .then((response) => {
        if (cancelled) return;

        /*
         * Every verdict below, and the order they are reached in, lives in
         * utils/kbArticleFetch.ts — the full-page article view on Knowledge Base (beta) has to
         * reach exactly the same ones, and one hand-written copy of that ordering was already
         * enough (ACOP-100). The reasoning that used to sit here moved with it; read
         * classifyArticleResponse before changing any of it.
         */
        const outcome = classifyArticleResponse(response);

        if (outcome.kind === "error") {
          setError(outcome.message);
          setLoading(false);
          return;
        }

        if (outcome.kind === "dead") {
          markCurrentDead();
          return;
        }

        const prepared = prepareArticleHtml(outcome.html, DOMPurify.sanitize);
        const doc = new DOMParser().parseFromString(prepared.html, "text/html");
        const heading = doc.querySelector("h1,h2,h3");
        if (heading?.textContent?.trim()) setArticleTitle(heading.textContent.trim());
        setHtml(prepared.html);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(ARTICLE_MESSAGES.failed);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [current, isDead, markCurrentDead]);

  /*
   * Intercept every link click in the panel.
   *
   * The hard requirement is that no click here leaves Copilot (ACOP-93). prepareArticleHtml has
   * already neutralised anything that could navigate, so the final branch below should be
   * unreachable — it exists because "should be unreachable" is not a guarantee, and the cost of
   * being wrong is a customer-facing app dropping the reader onto another site.
   */
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !html) return undefined;
    const handler = (e) => {
      // LINK_ELEMENTS, not "a" — an <area> in an image map is a link, and for a map click
      // e.target IS the <area>, so closest() has to be told to look for it.
      const a = e.target.closest(LINK_ELEMENTS);
      if (!a) return;

      // Resolved by the proxy — in-panel navigation to another article.
      const citationPath = a.getAttribute(CITATION_ATTR);
      if (citationPath) {
        e.preventDefault();
        /*
         * Scroll only when the fragment belongs to the article being read. Matching on the
         * fragment alone would scroll in place whenever a link to ANOTHER article happened to
         * name an id this one also has, silently swallowing the navigation.
         */
        const hashIdx = citationPath.indexOf("#");
        if (hashIdx > -1) {
          const samePath = citationPath.slice(0, hashIdx) === (current.path || "").split("#")[0];
          const anchor = citationPath.slice(hashIdx + 1);
          if (samePath || hashIdx === 0) {
            const target = el.querySelector(
              `[id="${escapeSelectorValue(anchor)}"], [name="${escapeSelectorValue(anchor)}"]`
            );
            if (target) {
              target.scrollIntoView({ behavior: "smooth", block: "start" });
              return;
            }
          }
        }
        push({
          kind: "article",
          url: environment.BASE_URL + "copilot/avs-os/citation?url=" + encodeURIComponent(citationPath),
          path: citationPath,
          label: (a.textContent || "").trim() || "Knowledge Base",
        });
        return;
      }

      // Marked unresolvable — either by AVSOS at ingest, or by prepareArticleHtml because nothing
      // vouched for the target. Both land on the in-panel dead page.
      const missing = a.getAttribute(DEAD_LINK_ATTR);
      if (missing !== null) {
        e.preventDefault();
        push({ kind: "dead", ...describeDeadLink(missing, a.textContent || "") });
        return;
      }

      const href = a.getAttribute("href");

      // No href attribute at all — a jump target such as <a id="top" name="top">, not a link.
      if (href === null) return;

      // In-page anchor scroll. Also swallows a bare "#", which would otherwise jump the outer page.
      if (href.startsWith("#")) {
        e.preventDefault();
        const targetId = href.slice(1);
        if (targetId) {
          const target = el.querySelector(
            `[id="${escapeSelectorValue(targetId)}"], [name="${escapeSelectorValue(targetId)}"]`
          );
          if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      // Nothing above claimed it and it can still navigate. Fail closed.
      if (isNavigatingHref(href)) {
        e.preventDefault();
        push({ kind: "dead", ...describeDeadLink(href, a.textContent || "") });
      }
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [html, push, current]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div className="c2-panel-backdrop" onClick={onClose} />

      {/* Side panel */}
      <div className="c2-panel" role="dialog" aria-modal="true" aria-label={articleTitle}>
        <div className="c2-panel-header">
          {canGoBack && (
            <button
              type="button"
              className="c2-panel-btn"
              onClick={goBack}
              aria-label="Go back"
            >
              ← Back
            </button>
          )}
          <span className="c2-panel-title" title={articleTitle}>{articleTitle}</span>
          {/*
            "Open full article" is a BUTTON that asks the owner to change route, never a link.

            There is still no "open in new tab" affordance and there must not be one. It used to
            point at the citation proxy with target="_blank", which is a click inside the slide-out
            that leaves Copilot — and inside the Salesforce utility-bar iframe it drops the reader
            on a bare HTML page with no way back. ACOP-93 requires that no click here leaves the
            app, and ACOP-100 honours that: an SPA route change to /knowledge-base-2 stays inside
            Copilot, so the reader keeps the nav, the chat and the way back.

            Rendered only once `html` is set. Offering the full view while the article is still
            loading, or after it failed, sends the reader to a page that will fail the same way.
          */}
          {onOpenFullArticle && fullArticleUrlname && html && (
            <button
              type="button"
              className="c2-panel-btn c2-panel-open"
              onClick={() => onOpenFullArticle(fullArticleUrlname)}
              data-testid="c2-open-full-article"
            >
              Open full article
            </button>
          )}
          <button
            type="button"
            className="c2-panel-btn c2-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="c2-panel-body" ref={contentRef}>
          {isDead ? (
            <DeadArticlePage entry={current} canGoBack={canGoBack} onBack={goBack} />
          ) : (
            <>
              {loading && (
                <div className="c2-panel-loading">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </div>
              )}
              {error && (
                <div className="c2-panel-content">
                  <p className="c2-article-error">{error}</p>
                </div>
              )}
              {html && (
                <div className="c2-panel-content" dangerouslySetInnerHTML={{ __html: html }} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
